import classNames from 'classnames'
import { useRouter } from 'next/router'
import {
  useEffect,
  useRef,
  type FC,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react'
import { FaTimes } from 'react-icons/fa'
import { Modal } from 'react-responsive-modal'
import 'react-responsive-modal/styles.css'

type ModalProps = {
  children: ReactElement
  isOpen: boolean
  id: string
  triggerCloseModal: VoidFunction
  showCloseIcon?: boolean
  fullSize?: boolean
  initialFocusRef?: RefObject<HTMLElement | null>
  variant?: 'center' | 'bottom-sheet'
  /** Optional interactive handle for a bottom sheet. */
  sheetHandle?: ReactNode
}

const GenericModal: FC<ModalProps> = ({
  children,
  isOpen = false,
  id,
  triggerCloseModal,
  fullSize = false,
  initialFocusRef = undefined,
  showCloseIcon = true,
  variant = 'center',
  sheetHandle,
}) => {
  const router = useRouter()
  const closeIconRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleRouteChange = () => {
      triggerCloseModal()
    }
    router.events.on('routeChangeStart', handleRouteChange)
    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [])

  return (
    <>
      <style>
        {`
          .dark .generic-modal {
            background: var(--color-primary-dark-400);
          }
          @media screen and (max-width: 767px) {
            .generic-modal {
              margin: 0;
              padding: 0;
              width: 100vw;
              max-width: 100vw;
              min-height: 100dvh;
              border-radius: 0;
            }
            .generic-modal.bottom-sheet {
              margin: auto 0 0 0;
              padding: 1.5rem;
              padding-top: 2rem;
              width: 100vw;
              max-width: 100vw;
              min-height: auto;
              border-radius: 2rem 2rem 0 0;
              background: var(--bg-main);
              border: 1px solid var(--border-main);
              border-bottom: none;
            }
            .dark .generic-modal.bottom-sheet {
              background: var(--color-primary-dark-500);
              border-color: var(--color-gray-800);
            }
          }
          .generic-modal.full-size {
            margin: 0;
            padding: 1.5rem;
            width: 100vw;
            height: 100vh;
            max-width: none;
            border-radius: 0;
          }
          @media screen and (min-width: 768px) {
            .generic-modal {
              width: 650px;
              min-width: auto;
              min-height: 500px;
              height: auto;
            }
            .generic-modal.full-size {
              width: 800px;
              height: 80vh;
              max-width: 90vw;
              border-radius: 1rem;
              margin: auto;
            }
            .generic-modal.bottom-sheet {
              width: 650px;
              border-radius: 1.5rem;
              margin-top: auto;
              min-height: auto;
            }
          }
          /*
           * The scroll lock (body-scroll-lock) watches the modal container, not the sheet.
           * On iOS it blocks every touch move unless the container itself can scroll, so the
           * container scrolls a sheet of natural height instead of the sheet scrolling inside.
           * The top padding keeps a 10dvh gap above the sheet; tapping it closes the sheet.
           */
          .generic-modal-container.bottom-sheet-container {
            padding-top: 10dvh;
            overscroll-behavior-y: contain;
          }
          .generic-modal.bottom-sheet {
            max-height: none;
          }
        `}
      </style>
      <Modal
        classNames={{
          modalContainer: classNames('generic-modal-container max-md:p-0', {
            // The sheet's top auto margin keeps it at the bottom; it collapses for tall sheets so their top stays reachable.
            '!flex bottom-sheet-container': variant === 'bottom-sheet',
          }),
          modal: classNames('generic-modal md:rounded-2xl md:p-4 outline-none', {
            'full-size': fullSize && variant !== 'bottom-sheet',
            'bottom-sheet animate-slide-up': variant === 'bottom-sheet',
          }),
        }}
        modalId={id}
        open={isOpen}
        blockScroll={true}
        showCloseIcon={showCloseIcon}
        initialFocusRef={(initialFocusRef || closeIconRef) as RefObject<HTMLElement>}
        onClose={triggerCloseModal}
        closeIcon={
          <span ref={closeIconRef}>
            <FaTimes className="text-2xl" />
          </span>
        }
      >
        {variant === 'bottom-sheet' &&
          (sheetHandle ?? (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full md:hidden" />
          ))}
        {children}
      </Modal>
    </>
  )
}

export default GenericModal
